import { useEffect, useState } from 'react';
import { CLAIM_KIND_LABELS, ClaimKind, WEEKLY_CLAIMS, WEEKLY_PHOTO } from '../content/weeklyClaimLens';
import './ClaimLensPage.css';

const shareUrl = () => `${window.location.origin}${window.location.pathname}#/lens`;

export default function ClaimLensPage() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState<ClaimKind[]>([]);
    const [shareMessage, setShareMessage] = useState('');
    const complete = step >= WEEKLY_CLAIMS.length;
    const claim = WEEKLY_CLAIMS[step];
    const selected = answers[step];
    const score = answers.filter((answer, index) => answer === WEEKLY_CLAIMS[index].kind).length;

    useEffect(() => {
        const priorTitle = document.title;
        const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
        const priorDescription = description?.content;
        document.title = '이번 주 3문장 읽기 | TruthPick';
        if (description) description.content = '실제 커뮤니티 글 세 문장을 읽고 확인 가능한 주장, 미래 예측, 비유를 구분해 보세요. 가입 없이 바로 참여할 수 있습니다.';
        return () => {
            document.title = priorTitle;
            if (description && priorDescription !== undefined) description.content = priorDescription;
        };
    }, []);

    useEffect(() => {
        if (complete) document.querySelector('.lens-result')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }, [complete]);

    const choose = (kind: ClaimKind) => {
        if (selected) return;
        setAnswers((current) => [...current, kind]);
    };

    const next = () => {
        if (!selected) return;
        if (step === WEEKLY_CLAIMS.length - 1) {
            void fetch('/api/analytics/events', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ eventType: 'claim_lens_completed', contentKey: '2026-10-24' }),
                keepalive: true
            }).catch(() => undefined);
        }
        setStep((current) => current + 1);
    };

    const share = async () => {
        const url = shareUrl();
        const text = `TruthPick 이번 주 3문장 읽기에서 ${score}개를 구분했어요. 같은 문장을 읽어볼래요?`;
        try {
            if (navigator.share) {
                await navigator.share({ title: 'TruthPick 이번 주 3문장 읽기', text, url });
                setShareMessage('공유했습니다.');
            } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(`${text}\n${url}`);
                setShareMessage('공유 문구와 링크를 복사했습니다.');
            } else {
                setShareMessage('아래 주소를 복사해 공유하세요.');
            }
        } catch { setShareMessage('공유를 취소했거나 복사하지 못했습니다. 아래 주소를 직접 복사할 수 있습니다.'); }
    };

    return <main className="lens-page">
        <header className="lens-hero">
            <div><span className="lens-kicker">THE WEEKLY READING / 2026.10.24</span><h1>세 문장을 읽고,<br />먼저 질문을 고르세요.</h1><p>진실인지 누르기 전에 이 문장이 어떤 종류인지 살펴보세요. 실제 커뮤니티 글에서 뽑은 세 문장으로, 가입 없이 2분이면 끝납니다.</p><button type="button" className="lens-start" onClick={() => document.getElementById('lens-workspace')?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })}>바로 시작하기 ↓</button></div>
            <div className="lens-hero-note"><span>읽는 방법</span><strong>확인할 수 있는가?<br />앞날을 예상하는가?<br />상상으로 말하는가?</strong><p>이 도전은 글의 사실 여부를 판정하거나 작성자의 의도를 단정하지 않습니다.</p></div>
        </header>

        {!complete ? <div className="lens-workspace" id="lens-workspace" aria-live="polite">
            <section className="lens-card" aria-labelledby="lens-question-title">
                <div className="lens-progress"><span>문장 {step + 1} / {WEEKLY_CLAIMS.length}</span><div aria-hidden="true"><i style={{ width: `${((step + 1) / WEEKLY_CLAIMS.length) * 100}%` }} /></div></div>
                <span className="lens-source">실제 커뮤니티 글 · {claim.category}</span>
                <h2 id="lens-question-title">“{claim.quote}”</h2>
                <p>이 문장은 무엇을 말하고 있을까요?</p>
                <div className="lens-choices">{(Object.keys(CLAIM_KIND_LABELS) as ClaimKind[]).map((kind) => <button key={kind} type="button" disabled={Boolean(selected)} className={`${selected === kind ? 'selected' : ''} ${selected && claim.kind === kind ? 'answer' : ''}`} onClick={() => choose(kind)}><span>{CLAIM_KIND_LABELS[kind]}</span><b aria-hidden="true">↗</b></button>)}</div>
                {selected && <div className="lens-feedback" role="status"><span>{selected === claim.kind ? '좋아요, 문장의 성격을 구분했어요.' : `이 문장은 ‘${CLAIM_KIND_LABELS[claim.kind]}’에 더 가깝습니다.`}</span><p>{claim.explanation}</p><div><a href={`#/board?post=${claim.postId}`}>원문과 의견 보기 ↗</a><button type="button" onClick={next}>{step === WEEKLY_CLAIMS.length - 1 ? '결과 보기' : '다음 문장'}</button></div></div>}
            </section>
            <aside className="lens-side"><span>WHY IT MATTERS</span><h2>판단은 분류에서<br />시작됩니다.</h2><p>같은 말이라도 확인할 자료가 필요한지, 앞으로의 변화에 대한 생각인지, 재미있는 비유인지에 따라 대화의 방식이 달라집니다.</p><div><strong>세 문장 모두 실제 게시글에서 골랐습니다.</strong><a href="#/board">전체 이야기 둘러보기 ↗</a></div></aside>
        </div> : <div className="lens-result" aria-live="polite">
            <div className="lens-result-top"><span>READING COMPLETE / YOUR RESULT</span><h2>{WEEKLY_CLAIMS.length}개 중 {score}개를 구분했어요.</h2><p>중요한 건 빠른 정답보다, 무엇을 확인해야 할지 알아차리는 일입니다. 원문으로 돌아가 다른 사람의 질문도 살펴보세요.</p><div className="lens-result-actions"><button type="button" onClick={share}>내 결과 공유하기 ↗</button><button type="button" onClick={() => { setStep(0); setAnswers([]); setShareMessage(''); }}>다시 읽기</button></div>{shareMessage && <p className="lens-share-message" role="status">{shareMessage}</p>}<label className="lens-share-url">직접 링크<input readOnly value={shareUrl()} onFocus={(event) => event.target.select()} /></label></div>
            <div className="lens-result-bottom"><section aria-label="읽은 글"><span>THREE SOURCE STORIES</span><h3>원문에서 계속 읽기</h3>{WEEKLY_CLAIMS.map((item) => <a href={`#/board?post=${item.postId}`} key={item.postId}><strong>{item.quote}</strong><small>{CLAIM_KIND_LABELS[item.kind]} · 의견 보기 ↗</small></a>)}</section><a className="lens-photo-next" href={WEEKLY_PHOTO.href}><img src={WEEKLY_PHOTO.imageUrl} alt={WEEKLY_PHOTO.alt} loading="lazy" /><div><span>ONE MORE WAY TO LOOK</span><strong>사진 속 단서도<br />구분해 볼까요?</strong><small>이번 주 운영자 제작 사진에서 보이는 것과 짐작한 것을 나눠보세요. ↗</small></div></a></div>
        </div>}
    </main>;
}
